import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import {
  Home,
  NotFound,
  PrivacyPolicy,
  UnauthorizedPage,
} from "features/home/presentation/pages";
import { REACT_APP_BASE_NAME, theme } from "features/shared/constants";
import { store } from "features/config/store";
import {
  PopClub,
  PopClubDeal,
  PopClubHome,
  PopClubIndexPage,
  PopclubOrder,
} from "features/popclub/presentation/pages";
import {
  Shop,
  ShopCheckout,
  ShopHome,
  ShopOrder,
  ShopPrivacyPolicy,
  ShopProduct,
  ShopProducts,
  ShopReturnPolicy,
  ShopTermsAndConditions,
  ShopEditCartItem,
} from "features/shop/presentation/pages";
import {
  Catering,
  CateringBuildYourOwnPackage,
  CateringCheckout,
  CateringContract,
  CateringOrder,
  CateringProduct,
  CateringProducts,
} from "features/catering/presentation/pages";
import { Franchising } from "features/franchising/presentation/pages";
import { Branches, BranchesHome } from "features/branches/presentation/pages";
import {
  AdminShopOrder,
  AdminLogin,
  AdminCateringBooking,
  AdminPopclub,
  AdminReports,
  AdminFaq,
  AdminCFaq,
  AdminSettingUser,
  AdminSettingStore,
  AdminSettingCreateUser,
  AdminSettingCreateGroup,
  AdminSettingEditUser,
  AdminAvailabilityDeal,
  AdminAvailabilityProduct,
  AdminAvailabilityCatersPackage,
  AdminAvailabilityCatersPackageAddon,
  AdminAvailabilityCatersProductAddon,
  AdminUserDiscount,
  AdminSurveyVerification,
  AdminSettingShopProduct,
  AdminSettingShopCreateProduct,
  AdminSettingShopCopyProduct,
  AdminSettingShopEditProduct,
  AdminSettingCreateStore,
  AdminAvailabilityCatersProduct,
  AdminSettingEditStore,
  AdminSettingCateringPackage,
  AdminSettingCateringCreatePackage,
  AdminSettingCateringEditPackage,
  AdminSettingCateringCopyPackage,
  AdminSettingPopClubDeal,
  AdminSettingPopclubCreateDeal,
  AdminSettingPopclubEditDeal,
  AdminInfluencerApplication,
  AdminInfluencerPromo,
  AdminInfluencerCreatePromo,
  AdminInfluencerCashout,
  AdminSnackshopDashboard,
  AdminCustomerFeedbackDashboard,
  AdminLandingPage,
} from "features/admin/presentation/pages";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { CateringHome } from "features/catering/presentation/pages/catering-home.page";
import {
  ConsentWrapper,
  LoadingAndSnackbarWrapper,
  UserNotificationWrapper,
  AnalyticsWrapper,
  SessionWrapper,
  LoginChooserWrapper,
  MessageModalWrapper,
} from "features/shared/presentation/wrappers";
import {
  ShopCheckoutGuard,
  ShopProductsGuard,
} from "features/shop/presentation/guards";
import { ProfileGuard } from "features/profile/presentation/guards";

import { Admin } from "features/admin/presentation/pages/admin.page";
import { AdminGuard } from "features/admin/presentation/guards/admin.guard";
import {
  Profile,
  ProfileCateringBookings,
  ProfileHome,
  ProfileSnackshopOrders,
  ProfilePopclubRedeems,
  ProfileInbox,
  ProfileInfluencer,
} from "features/profile/presentation/pages";
import { ProfileUserDiscount } from "features/profile/presentation/pages";
import { Bsc } from "features/bsc/presentation/pages/bsc.page";

import {
  Survey,
  SurveyLogin,
  SurveyComplete,
  SurveySnackshop,
  SurveySnackshopComplete,
} from "features/survey/presentation/pages";

import {
  SurveyHr,
  SurveySuccess,
} from "features/emphrsurvey/presentation/pages";

import {
  BSCLogin,
  BscSignUp,
  BscDashboard,
  BSCEditUser,
  BSCCreateUser,
  BSCUser,
  BSCCreateGroup,
} from "features/bsc/presentation/pages";
import {
  TermsAndConditionModal,
  ReturnPolicyModal,
  PrivacyPolicyModal,
} from "features/bsc/presentation/modals";

import { BSCSidebarWrapper } from "features/bsc/presentation/components/bsc-sidebar-wrapper";
import { BscGuard } from "features/bsc/presentation/guards/bsc.guard";
import {
  SeeMeCatering,
  SeeMeSnackshop,
  SeeMePopClub,
} from "features/see-me/presentation/pages";
import { SeeMeReseller } from "features/see-me/presentation/pages/see-me-reseller.page";
import { ShopSurveyGuard } from "features/survey/presentation/guards";
import {
  CateringCheckoutGuard,
  CateringProductsGuard,
} from "features/catering/presentation/guards";
import {
  AdminNotificationWrapper,
  AdminSidebarWrapper,
} from "features/admin/presentation/wrapper";
import {
  Audit,
  AuditDashboard,
  AuditForm,
  AuditLogin,
  AuditResponseQualityAuditPage,
  AuditReview,
  AuditSettingsQuestions,
} from "features/audit/presentation/pages";
import {
  AuditFormSideStepper,
  AuditSidebarWrapper,
} from "features/audit/presentation/components";
import { AuditGuard } from "features/audit/presentation/guards/audit.guard";
import { ShopProductViewLog } from "features/shop/presentation/logs";
import {
  StockNotificationWrapper,
  StockOrderingBadgeWrapper,
  StockOrderProcessWrapper,
  StockSidebarWrapper,
} from "features/stock-ordering/presentation/components";
import {
  StockOrderOrders,
  StockOrderingSettingsCreateProductsPage,
  StockOrderingSettingsEditProductsPage,
  StockOrderingSettingsProductsPage,
  StockOrderSettingsGeneralPage,
  TransferPage,
  StockSellingPage,
  ProductRateForm,
  ProductReviewsPage,
  ProcurementPage,
  MaintenancePage,
  StockTermsAndConditions,
  NoticesAdminPage,
} from "features/stock-ordering/presentation/pages";
import {
  Hr,
  Hr180DegreeAssessment,
  Hr180DegreeAssessmentAnswers,
  HrDashboard,
  HrEmployees,
  HrLogin,
  HrManagementAssessment,
  HrSelfAssessment,
  HrStaffAssessmentAnswers,
  HrUserEmployees,
} from "features/hr/presentation/pages";
import { HrGuard } from "features/hr/presentation/guards/hr.guard";
import { HrSidebarWrapper } from "features/hr/presentation/components/hr-sidebar-wrapper";
import { HrJobDetails } from "features/hr/presentation/pages/hr-job-details.page";
import { HrContactDetails } from "features/hr/presentation/pages/hr-contact-details.page";
import { HrPersonalDetails } from "features/hr/presentation/pages/hr-personal-details.page";
import { HrEmergencyDetails } from "features/hr/presentation/pages/hr-emergency-details.page";
import { HrSalaryDetails } from "features/hr/presentation/pages/hr-salary-details.page";
import { HrTerminationDetails } from "features/hr/presentation/pages/hr-termination-details.page";
import { HrOtherDetails } from "features/hr/presentation/pages/hr-other-details.page";
import { HrAssessmentSummary } from "features/hr/presentation/pages/hr-assessment-summary.page";

import { AllTickets } from "features/svr/presentation/pages";
import { TicketingSidebarWrapper } from "features/svr/presentation/components/sidebar";
import { CateringFreeItemModalWrapper } from "features/catering/presentation/wrappers/catering-free-item-modal.wrapper";
import { CateringDefaultWrappers } from "features/catering/presentation/wrappers/catering-default-wrappers";
import { CateringSelectTypeWrapper } from "features/catering/presentation/wrappers/catering-select-type-modal.wrapper";
import { Hr180ViewingDashboardPage } from "features/hr/presentation/pages/hr-180-viewing-dashboard.page";
import { HrAssessmentAnswers } from "features/hr/presentation/pages/hr-assessment-answers.page";
import { HrEmployeesImport } from "features/hr/presentation/pages/hr-employees-import-users";

import { StockOrderingPermissionGuard } from "features/stock-ordering/presentation/guard";
import { LandingPageGuardGuard } from "features/admin/presentation/guards/landing-page.guard";
import {
  HubLink,
  TeiHubLinkCancelled,
  TeiHubLinkCompleted,
  TeiHubLinkCreateNewOrderPage,
  TeiHubLinkCreateUserPage,
  TeiHubLinkLogisticReceiveMultipleOrdersPage,
  TeiHubLinkLogin,
  TeiHubLinkOrderInformation,
  TeiHubLinkPage,
  TeiHubLinkSoaPage,
  TeiHubLinkCreateStorePage,
} from "features/hublink/presentation/pages";
import {
  TeiHubLinkClientReceivedOrder,
  TeiHubLinkRealTimeWrapper,
} from "features/hublink/presentation/components";
import { HubLinkGuard } from "features/hublink/presentation/guard/hublink.guard";
import { TeiHubLinkSidebarWrapper } from "features/hublink/presentation/components/sidebar";
import { TeiHubLinkLoginModal } from "features/hublink/presentation/modals";
import {
  Ms6Page,
  PurchaseRequisitionPage,
} from "features/ms6/presentation/pages";
import { Ms6SidebarWrapper } from "features/ms6/presentation/components/sidebar/ms6-sidebar-wrapper";
import { FormManpower } from "features/ms6/presentation/pages/form.manpower";
import { FormMachines } from "features/ms6/presentation/pages/form.machines";
import { FormMaterials } from "features/ms6/presentation/pages/form.materials";
import { FormMethods } from "features/ms6/presentation/pages/form.methods";
import { FormMarketing } from "features/ms6/presentation/pages/form.marketing";
import { FormMoney } from "features/ms6/presentation/pages/form.money";
import { FormList } from "features/ms6/presentation/pages/form.list";
import { FormManpowerAttendance } from "features/ms6/presentation/pages/form.manpowerAttendance";
import { FormManpowerIncident } from "features/ms6/presentation/pages/form.manpowerIncident";
import { FormMaterialsInventory } from "features/ms6/presentation/pages/form.materialsInventory";
import { FormMaterialsEfficiency } from "features/ms6/presentation/pages/form.materialsEfficiency";
import { CreateGiftCertificates } from "features/ms6/presentation/components/create-gift-certificates";
import { GiftCertificateDashboard } from "features/ms6/presentation/components/gift-certificate-dashboard";
import { ManageGiftCertificates } from "features/ms6/presentation/components/manage-gift-certificates";
import { StoreGroupsManagement } from "features/ms6/presentation/components/store-groups-management";
import ChecklistBuilder from "features/ms6/presentation/pages/checklist.builder";
import HelloWorld from "features/ms6/presentation/pages/checklist.builder";
import { CompanyManagement } from "features/ms6/presentation/components/company-management";
import { SetStoreStatus } from "features/ms6/presentation/components/set-store-status";
import { StoreStatusDashboard } from "features/ms6/presentation/components/store-status-dashboard";
import { StoreStatusHistory } from "features/ms6/presentation/components/store-status-history";
const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

if (window.location.hash === "#_=_") {
  window.location.replace(
    window.location.protocol +
      "//" +
      window.location.host +
      window.location.pathname +
      window.location.search
  );
}

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <BrowserRouter basename={REACT_APP_BASE_NAME}>
          <Routes>
            <Route element={<AnalyticsWrapper />}>
              <Route element={<ConsentWrapper />}>
                <Route element={<SessionWrapper />}>
                  <Route element={<LoadingAndSnackbarWrapper />}>
                    <Route element={<MessageModalWrapper />}>
                      <Route element={<LoginChooserWrapper />}>
                        <Route element={<UserNotificationWrapper />}>
                          <Route path="/" element={<Home />} />
                          <Route path="branches" element={<Branches />}>
                            <Route index element={<BranchesHome />} />
                          </Route>

                          <Route path="franchising" element={<Franchising />} />

                          <Route path="see_me">
                            <Route index element={<SeeMeSnackshop />} />
                            <Route
                              path="catering"
                              element={<SeeMeCatering />}
                            />
                            <Route path="popclub" element={<SeeMePopClub />} />
                            <Route
                              path="reseller"
                              element={<SeeMeReseller />}
                            />
                          </Route>

                          <Route
                            path="privacy-policy"
                            element={<PrivacyPolicy />}
                          />

                          <Route path="profile" element={<Profile />}>
                            <Route element={<ProfileGuard />}>
                              <Route index element={<ProfileHome />} />
                              <Route path="inbox" element={<ProfileInbox />} />
                              <Route
                                path="snackshop-orders"
                                element={<ProfileSnackshopOrders />}
                              />
                              <Route
                                path="catering-bookings"
                                element={<ProfileCateringBookings />}
                              />
                              <Route
                                path="popclub-redeems"
                                element={<ProfilePopclubRedeems />}
                              />

                              <Route
                                path="user-discount"
                                element={<ProfileUserDiscount />}
                              />

                              <Route
                                path="influencer"
                                element={<ProfileInfluencer />}
                              />
                            </Route>
                          </Route>

                          <Route path="popclub" element={<PopClub />}>
                            <Route index element={<PopClubIndexPage />} />
                            <Route path=":platform" element={<PopClubHome />} />
                            <Route
                              path="deal/:hash"
                              element={<PopClubDeal />}
                            />
                            <Route
                              path="order/:hash"
                              element={<PopclubOrder />}
                            />
                          </Route>

                          <Route path="delivery" element={<Shop />}>
                            <Route index element={<ShopHome />} />

                            <Route path="products">
                              <Route element={<ShopProductsGuard />}>
                                <Route index element={<ShopProducts />} />
                              </Route>
                              <Route
                                path="cart/:cart_id"
                                element={<ShopEditCartItem />}
                              />
                              <Route element={<ShopProductViewLog />}>
                                <Route path=":hash" element={<ShopProduct />} />
                              </Route>
                            </Route>

                            <Route path="order/:hash" element={<ShopOrder />} />

                            <Route element={<ShopCheckoutGuard />}>
                              <Route
                                path="checkout"
                                element={<ShopCheckout />}
                              />
                            </Route>
                            <Route
                              path="terms-and-conditions"
                              element={<ShopTermsAndConditions />}
                            />

                            <Route
                              path="privacy-policy"
                              element={<ShopPrivacyPolicy />}
                            />
                            <Route
                              path="return-policy"
                              element={<ShopReturnPolicy />}
                            />
                          </Route>

                          <Route
                            path="catering"
                            element={<Navigate to={"/shop"} />}
                          />
                          <Route
                            path="shop/admin"
                            element={<Navigate to={"/admin"} />}
                          />
                          <Route
                            path="shop/login"
                            element={<Navigate to={"/admin"} />}
                          />
                          <Route element={<CateringDefaultWrappers />}>
                            <Route element={<CateringFreeItemModalWrapper />}>
                              <Route path="shop" element={<Catering />}>
                                <Route element={<CateringSelectTypeWrapper />}>
                                  <Route index element={<CateringHome />} />
                                </Route>

                                <Route path="products">
                                  <Route element={<CateringProductsGuard />}>
                                    <Route
                                      index
                                      element={<CateringProducts />}
                                    />
                                    <Route
                                      path="build-your-own-package"
                                      element={<CateringBuildYourOwnPackage />}
                                    />
                                  </Route>

                                  <Route
                                    path=":hash"
                                    element={<CateringProduct />}
                                  />
                                </Route>

                                <Route
                                  path="contract/:hash"
                                  element={<CateringContract />}
                                />

                                <Route element={<CateringCheckoutGuard />}>
                                  <Route
                                    path="checkout"
                                    element={<CateringCheckout />}
                                  />
                                </Route>

                                <Route
                                  path="order/:hash"
                                  element={<CateringOrder />}
                                />
                              </Route>
                            </Route>
                          </Route>

                          <Route path="feedback">
                            <Route index element={<SurveyLogin />} />

                            <Route path="walk-in" element={<Survey />} />

                            <Route
                              path="complete/:hash"
                              element={<SurveyComplete />}
                            />

                            <Route
                              path="snackshop/complete/:hash"
                              element={<SurveySnackshopComplete />}
                            />

                            <Route element={<ShopSurveyGuard />}>
                              <Route
                                path="snackshop/:hash"
                                element={<SurveySnackshop />}
                              />
                            </Route>
                          </Route>

                          <Route path="emphrsurvey">
                            <Route path="" element={<SurveyHr />} />
                            <Route path="success" element={<SurveySuccess />} />
                          </Route>
                        </Route>
                      </Route>

                      <Route path="admin" element={<Admin />}>
                        <Route index element={<AdminLogin />} />
                        <Route element={<AdminNotificationWrapper />}>
                          {/* <Route
                            path="landing"
                            element={<AdminLandingPage />}
                          /> */}
                          <Route element={<LandingPageGuardGuard />}>
                            <Route
                              path="landing"
                              element={<AdminLandingPage />}
                            />
                          </Route>
                          <Route element={<AdminGuard />}>
                            <Route element={<AdminSidebarWrapper />}>
                              <Route path="dashboard">
                                <Route
                                  path="snackshop"
                                  element={<AdminSnackshopDashboard />}
                                />
                                <Route
                                  path="customer-feedback"
                                  element={<AdminCustomerFeedbackDashboard />}
                                />
                              </Route>
                              <Route
                                path="order"
                                element={<AdminShopOrder />}
                              />
                              <Route
                                path="catering"
                                element={<AdminCateringBooking />}
                              />
                              <Route
                                path="popclub"
                                element={<AdminPopclub />}
                              />

                              <Route
                                path="user-discount"
                                element={<AdminUserDiscount />}
                              />
                              <Route path="influencer">
                                <Route
                                  path="application"
                                  element={<AdminInfluencerApplication />}
                                />
                                <Route
                                  path="cashout"
                                  element={<AdminInfluencerCashout />}
                                />
                                <Route path="promo">
                                  <Route
                                    index
                                    element={<AdminInfluencerPromo />}
                                  />
                                  <Route
                                    path="create"
                                    element={<AdminInfluencerCreatePromo />}
                                  />
                                </Route>
                              </Route>

                              <Route
                                path="survey-verification"
                                element={<AdminSurveyVerification />}
                              />
                              <Route
                                path="reports"
                                element={<AdminReports />}
                              />
                              <Route path="faq">
                                <Route index element={<AdminFaq />} />
                                <Route path="store" element={<AdminFaq />} />
                                <Route
                                  path="customer"
                                  element={<AdminCFaq />}
                                />
                              </Route>
                              <Route path="availability">
                                <Route path="shop">
                                  <Route
                                    path="product"
                                    element={<AdminAvailabilityProduct />}
                                  />
                                </Route>

                                <Route path="catering">
                                  <Route
                                    path="package"
                                    element={<AdminAvailabilityCatersPackage />}
                                  />
                                  <Route
                                    path="build-your-own-package"
                                    element={<AdminAvailabilityCatersProduct />}
                                  />
                                  <Route
                                    path="package-addon"
                                    element={
                                      <AdminAvailabilityCatersPackageAddon />
                                    }
                                  />

                                  <Route
                                    path="product-addon"
                                    element={
                                      <AdminAvailabilityCatersProductAddon />
                                    }
                                  />
                                </Route>

                                <Route path="popclub">
                                  <Route
                                    path="deal"
                                    element={<AdminAvailabilityDeal />}
                                  />
                                </Route>
                              </Route>

                              <Route path="setting">
                                <Route path="user">
                                  <Route index element={<AdminSettingUser />} />
                                  <Route
                                    path="create-user"
                                    element={<AdminSettingCreateUser />}
                                  />
                                  <Route
                                    path="edit-user/:id"
                                    element={<AdminSettingEditUser />}
                                  />
                                  <Route
                                    path="create-group"
                                    element={<AdminSettingCreateGroup />}
                                  />
                                </Route>
                                <Route path="store">
                                  <Route
                                    index
                                    element={<AdminSettingStore />}
                                  />
                                  <Route
                                    path="create-store"
                                    element={<AdminSettingCreateStore />}
                                  />
                                  <Route
                                    path=":id"
                                    element={<AdminSettingEditStore />}
                                  />
                                </Route>
                                <Route path="product">
                                  <Route
                                    index
                                    element={<AdminSettingShopProduct />}
                                  />

                                  <Route
                                    path="create-product"
                                    element={<AdminSettingShopCreateProduct />}
                                  />

                                  <Route
                                    path="copy/:id"
                                    element={<AdminSettingShopCopyProduct />}
                                  />

                                  <Route
                                    path=":id"
                                    element={<AdminSettingShopEditProduct />}
                                  />
                                </Route>

                                <Route path="package">
                                  <Route
                                    index
                                    element={<AdminSettingCateringPackage />}
                                  />

                                  <Route
                                    path="create-package"
                                    element={
                                      <AdminSettingCateringCreatePackage />
                                    }
                                  />
                                  <Route
                                    path="copy/:id"
                                    element={
                                      <AdminSettingCateringCopyPackage />
                                    }
                                  />

                                  <Route
                                    path=":id"
                                    element={
                                      <AdminSettingCateringEditPackage />
                                    }
                                  />
                                </Route>

                                <Route path="deal">
                                  <Route
                                    index
                                    element={<AdminSettingPopClubDeal />}
                                  />
                                  <Route
                                    path="create-deal"
                                    element={<AdminSettingPopclubCreateDeal />}
                                  />

                                  <Route
                                    path=":id"
                                    element={<AdminSettingPopclubEditDeal />}
                                  />
                                </Route>
                              </Route>
                            </Route>
                            <Route element={<TicketingSidebarWrapper />}>
                              <Route path="svr">
                                <Route
                                  path="dashboard"
                                  element={<AllTickets />}
                                />
                              </Route>
                            </Route>
                          </Route>

                          <Route path="ms6">
                            <Route element={<Ms6SidebarWrapper />}>
                              <Route
                                path="dashboard-page"
                                element={<Ms6Page />}
                              />
                              <Route path="dashboard" element={<Ms6Page />} />
                              <Route path="form-list" element={<FormList />} />
                              <Route path="manpower">
                                <Route
                                  path="daily-attendance"
                                  element={<FormManpowerAttendance />}
                                />
                                <Route
                                  path="incident-report"
                                  element={<FormManpowerIncident />}
                                />
                              </Route>
                              <Route
                                path="machines"
                                element={<FormMachines />}
                              />
                              <Route path="material">
                                <Route
                                  path="efficiency-metric"
                                  element={<FormMaterialsEfficiency />}
                                />
                                <Route
                                  path="inventory-levels"
                                  element={<FormMaterialsInventory />}
                                />
                                <Route
                                  path="purchase-requisition"
                                  element={<PurchaseRequisitionPage />}
                                />
                              </Route>
                              <Route path="methods" element={<FormMethods />} />
                              <Route
                                path="marketing"
                                element={<FormMarketing />}
                              />
                              <Route path="store-status">
                                <Route
                                  path="dashboard"
                                  element={<StoreStatusDashboard />}
                                />
                                <Route
                                  path="set-status"
                                  element={<SetStoreStatus />}
                                />
                                <Route
                                  path="history"
                                  element={<StoreStatusHistory />}
                                />
                              </Route>
                              <Route path="money" element={<FormMoney />} />
                              <Route
                                path="gift-certificates/dashboard"
                                element={<GiftCertificateDashboard />}
                              />
                              <Route
                                path="gift-certificates/create"
                                element={<CreateGiftCertificates />}
                              />
                              <Route
                                path="gift-certificates/manage"
                                element={<ManageGiftCertificates />}
                              />
                              <Route
                                path="gift-certificates/store-groups"
                                element={<StoreGroupsManagement />}
                              />
                              <Route
                                path="gift-certificates/company-management"
                                element={<CompanyManagement />}
                              />
                              <Route
                                path="checklist-builder"
                                element={<ChecklistBuilder />}
                              />
                            </Route>
                          </Route>

                          {/* Stock Maintenance */}
                          <Route
                            path="material/sorry"
                            element={<MaintenancePage />}
                          />

                          <Route element={<StockOrderingPermissionGuard />}>
                            <Route element={<StockNotificationWrapper />}>
                              <Route element={<StockOrderingBadgeWrapper />}>
                                <Route path="material">
                                  <Route element={<StockSidebarWrapper />}>
                                    <Route
                                      element={<StockOrderProcessWrapper />}
                                    >
                                      <Route
                                        path=":view"
                                        element={<StockOrderOrders />}
                                      />

                                      <Route
                                        path=":view/:orderId"
                                        element={<StockOrderOrders />}
                                      />
                                      <Route
                                        path=":view/:orderId/rate"
                                        element={<ProductRateForm />}
                                      />

                                      <Route
                                        path=":view/reviews/:productId"
                                        element={<ProductReviewsPage />}
                                      />
                                    </Route>

                                    <Route
                                      path=":view/terms-and-condition"
                                      element={<StockTermsAndConditions />}
                                    />

                                    <Route
                                      path=":view/products"
                                      element={
                                        <StockOrderingSettingsProductsPage />
                                      }
                                    />

                                    <Route
                                      path=":view/products/create"
                                      element={
                                        <StockOrderingSettingsCreateProductsPage />
                                      }
                                    />
                                    <Route
                                      path=":view/products/edit/:id"
                                      element={
                                        <StockOrderingSettingsEditProductsPage />
                                      }
                                    />

                                    <Route
                                      path="transfer"
                                      element={<TransferPage />}
                                    />

                                    <Route path=":view/settings">
                                      <Route
                                        path="general-settings"
                                        element={
                                          <StockOrderSettingsGeneralPage />
                                        }
                                      />
                                      <Route
                                        path="notices"
                                        element={<NoticesAdminPage />}
                                      />
                                    </Route>
                                  </Route>
                                </Route>
                              </Route>
                            </Route>
                          </Route>
                        </Route>
                      </Route>

                      <Route path="internal" element={<Audit />}>
                        <Route index element={<AuditLogin />} />

                        <Route element={<AuditGuard />}>
                          <Route element={<AuditSidebarWrapper />}>
                            <Route
                              path="dashboard/audit"
                              element={<AuditDashboard />}
                            />

                            <Route path="responses">
                              <Route
                                path="quality/audit"
                                element={<AuditResponseQualityAuditPage />}
                              />
                            </Route>

                            <Route path="settings">
                              <Route
                                path="questions"
                                element={<AuditSettingsQuestions />}
                              />
                            </Route>

                            <Route
                              path="form/review/:hash"
                              element={<AuditReview />}
                            />
                          </Route>
                          <Route element={<AuditFormSideStepper />}>
                            <Route path="audit/form" element={<AuditForm />} />
                          </Route>
                        </Route>
                      </Route>

                      <Route path="hublink" element={<HubLink />}>
                        <Route index element={<TeiHubLinkLogin />} />
                        <Route
                          path="create-user"
                          element={<TeiHubLinkCreateUserPage />}
                        />
                        <Route
                          path="create-store"
                          element={<TeiHubLinkCreateStorePage />}
                        />

                        <Route element={<HubLinkGuard />}>
                          <Route element={<TeiHubLinkSidebarWrapper />}>
                            <Route element={<TeiHubLinkRealTimeWrapper />}>
                              <Route
                                path=":clientType"
                                element={<TeiHubLinkPage />}
                              />
                              <Route
                                path="dispatched/:clientType"
                                element={
                                  <TeiHubLinkLogisticReceiveMultipleOrdersPage />
                                }
                              />

                              <Route
                                path="completed/:clientType/"
                                element={<TeiHubLinkCompleted />}
                              />
                              <Route
                                path="cancelled/:clientType/"
                                element={<TeiHubLinkCancelled />}
                              />
                              <Route
                                path=":clientType/create-new-order"
                                element={<TeiHubLinkCreateNewOrderPage />}
                              />
                              <Route
                                element={<TeiHubLinkClientReceivedOrder />}
                              >
                                <Route
                                  path=":clientType/order/:trackingNum"
                                  element={<TeiHubLinkOrderInformation />}
                                >
                                  <Route path="modify/:edit" />
                                  <Route path="order-receive" />
                                </Route>
                              </Route>
                              <Route
                                path="soa/:clientType"
                                element={<TeiHubLinkSoaPage />}
                              />
                            </Route>
                          </Route>
                        </Route>
                      </Route>

                      <Route path="hr" element={<Hr />}>
                        <Route index element={<HrLogin />} />

                        <Route element={<HrGuard />}>
                          <Route element={<HrSidebarWrapper />}>
                            <Route path="dashboard" element={<HrDashboard />} />
                            <Route
                              path="user/job-details"
                              element={<HrJobDetails />}
                            />
                            <Route
                              path="user/contact-details"
                              element={<HrContactDetails />}
                            />
                            <Route
                              path="user/personal-details"
                              element={<HrPersonalDetails />}
                            />
                            <Route
                              path="user/emergency-details"
                              element={<HrEmergencyDetails />}
                            />
                            <Route
                              path="user/salary-details"
                              element={<HrSalaryDetails />}
                            />
                            <Route
                              path="user/termination-details"
                              element={<HrTerminationDetails />}
                            />
                            <Route
                              path="user/other-details"
                              element={<HrOtherDetails />}
                            />
                            <Route
                              path="self-assessment"
                              element={<HrSelfAssessment />}
                            />
                            <Route
                              path="180-degree-assessment"
                              element={<Hr180DegreeAssessment />}
                            />
                            <Route
                              path="management-assessment"
                              element={<HrManagementAssessment />}
                            />
                            <Route
                              path="staff-assessment-answer"
                              element={<HrStaffAssessmentAnswers />}
                            />
                            <Route
                              path="assessment-answer"
                              element={<HrAssessmentAnswers />}
                            />
                            <Route
                              path="180-degree-assessment-answer"
                              element={<Hr180DegreeAssessmentAnswers />}
                            />

                            <Route path="employees">
                              <Route index element={<HrEmployees />} />
                              <Route
                                path="import"
                                element={<HrEmployeesImport />}
                              />
                            </Route>

                            <Route
                              path="user/employees"
                              element={<HrUserEmployees />}
                            />

                            <Route
                              path="assessment-summary"
                              element={<HrAssessmentSummary />}
                            />
                            <Route
                              path="180-viewing-dashboard"
                              element={<Hr180ViewingDashboardPage />}
                            />
                          </Route>
                        </Route>
                      </Route>

                      <Route path="bsc" element={<Bsc />}>
                        <Route index element={<BSCLogin />} />
                        <Route path="sign-up" element={<BscSignUp />} />

                        <Route
                          path="terms-and-condition"
                          element={<TermsAndConditionModal />}
                        />
                        <Route
                          path="privacy-policy"
                          element={<PrivacyPolicyModal />}
                        />
                        <Route
                          path="return-policy"
                          element={<ReturnPolicyModal />}
                        />

                        <Route element={<BscGuard />}>
                          <Route element={<BSCSidebarWrapper />}>
                            <Route
                              path="dashboard"
                              element={<BscDashboard />}
                            />
                            <Route path="users">
                              <Route index element={<BSCUser />} />
                              <Route
                                path="create-user"
                                element={<BSCCreateUser />}
                              />
                              <Route
                                path="create-group"
                                element={<BSCCreateGroup />}
                              />
                              <Route
                                path="edit-user/:id"
                                element={<BSCEditUser />}
                              />
                            </Route>
                          </Route>
                        </Route>
                      </Route>
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
            <Route path="unauthorize" element={<UnauthorizedPage />} />
          </Routes>
        </BrowserRouter>
      </Provider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
